import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddCircleOutline, CheckCircle, InfoOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useGetAdminChatBotProfileQuery,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
} from "features/adminUsers/adminUsers";

const API_KEY_EMAIL = "konrad@hivemechanics.io";
const API_KEY_HINT = (
  <>
    To get a chat ID and API key for this user, send an email to{" "}
    <Link href={`mailto:${API_KEY_EMAIL}`} color="inherit" sx={{ fontWeight: 600 }}>
      {API_KEY_EMAIL}
    </Link>{" "}
    and then enter them below.
  </>
);
const CHATBOT_EDIT_TIP = "User & Folder Manager or Super Admin required to change Chat Bot config.";

interface Props {
  userId: number;
  canEdit?: boolean;
  onSuccess?: (message: string, severity: "success" | "error") => void;
}

export default function UserManagementChatBotTab({ userId, canEdit = true, onSuccess }: Props) {
  const { data: profile, isLoading, isError, error } = useGetAdminChatBotProfileQuery(userId);
  const [createProfile, { isLoading: isCreating }] = useCreateAdminChatBotProfileMutation();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateAdminChatBotProfileMutation();

  const [chatId, setChatId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [syncedFromProfile, setSyncedFromProfile] = useState(false);

  const noProfile = isError && (error as { status?: number })?.status === 404;
  const hasProfile = !isError && !!profile;

  useEffect(() => {
    setSyncedFromProfile(false);
  }, [userId]);

  useEffect(() => {
    if (hasProfile && profile) {
      setChatId(profile.chat_id ?? "");
      setApiKey(profile.api_key ?? "");
      setSyncedFromProfile(true);
    } else {
      setSyncedFromProfile(false);
    }
  }, [hasProfile, profile?.chat_id, profile?.api_key]);

  const handleCreate = async () => {
    try {
      await createProfile({ userId, chat_id: chatId || "pending", api_key: apiKey }).unwrap();
      onSuccess?.("Chat Bot profile created.", "success");
    } catch {
      onSuccess?.("Failed to create Chat Bot profile.", "error");
    }
  };

  const handleUpdate = async () => {
    try {
      const payload: { userId: number; chat_id?: string; api_key?: string } = {
        userId,
        chat_id: chatId || profile?.chat_id,
      };
      payload.api_key = apiKey;
      await updateProfile(payload).unwrap();
      onSuccess?.("Chat Bot config updated.", "success");
    } catch {
      onSuccess?.("Failed to update Chat Bot config.", "error");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
      {(noProfile || hasProfile) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 2,
            color: hasProfile ? "success.main" : "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          {hasProfile ? (
            <>
              <CheckCircle sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="success.main" fontWeight={600}>
                Profile saved — you can edit the config below
              </Typography>
            </>
          ) : (
            <>
              <AddCircleOutline sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                First-time setup — no Chat Bot profile yet
              </Typography>
            </>
          )}
        </Box>
      )}
      {noProfile && (
        <>
          <Alert
            icon={<InfoOutlined />}
            severity="info"
            sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
          >
            {API_KEY_HINT}
          </Alert>
          <Tooltip title={!canEdit ? CHATBOT_EDIT_TIP : ""} placement="top">
            <span>
              <TextField
                label="Chat ID"
                value={chatId}
                onChange={(e) => canEdit && setChatId(e.target.value)}
                placeholder="pending"
                size="small"
                fullWidth
                disabled={!canEdit}
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </span>
          </Tooltip>
          <Tooltip title={!canEdit ? CHATBOT_EDIT_TIP : ""} placement="top">
            <span>
              <TextField
                label="API Key"
                type={showApiKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => canEdit && setApiKey(e.target.value)}
                size="small"
                fullWidth
                disabled={!canEdit}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowApiKey((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        aria-label={showApiKey ? "Hide API key" : "Show API key"}
                        edge="end"
                      >
                        {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </span>
          </Tooltip>
          <Tooltip title={!canEdit ? CHATBOT_EDIT_TIP : ""} placement="top">
            <span>
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={!canEdit || isCreating}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
              >
                {isCreating ? "Creating…" : "Add Chat Bot Profile"}
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {hasProfile && profile && (
        <>
          <Alert
            icon={<InfoOutlined />}
            severity="info"
            sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
          >
            {API_KEY_HINT}
          </Alert>
          <Tooltip title={!canEdit ? CHATBOT_EDIT_TIP : ""} placement="top">
            <span>
              <TextField
                label="Chat ID"
                value={chatId || profile.chat_id}
                onChange={(e) => canEdit && setChatId(e.target.value)}
                size="small"
                fullWidth
                disabled={!canEdit}
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </span>
          </Tooltip>
          <Tooltip title={!canEdit ? CHATBOT_EDIT_TIP : ""} placement="top">
            <span>
              <TextField
                label="API Key"
                type={showApiKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => canEdit && setApiKey(e.target.value)}
                placeholder="Enter API key"
                size="small"
                fullWidth
                disabled={!canEdit}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowApiKey((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        aria-label={showApiKey ? "Hide API key" : "Show API key"}
                        edge="end"
                      >
                        {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </span>
          </Tooltip>
          <Tooltip
            title={
              !canEdit
                ? CHATBOT_EDIT_TIP
                : !syncedFromProfile
                  ? ""
                  : (chatId || profile.chat_id) === profile.chat_id && apiKey === (profile?.api_key ?? "")
                    ? "No changes to save"
                    : ""
            }
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={
                  !canEdit ||
                  isUpdating ||
                  !syncedFromProfile ||
                  ((chatId || profile.chat_id) === profile.chat_id && apiKey === (profile?.api_key ?? ""))
                }
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
              >
                {isUpdating ? "Saving…" : "Save Changes"}
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {!noProfile && !hasProfile && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          Unable to load chat bot profile. Please try again.
        </Typography>
      )}
    </Box>
  );
}
